import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, X, BarChart3, Bot } from "lucide-react";

const IMG = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/help/image/${p}`;
const ROLES = ["owner", "manager", "cashier"];
const blank = () => ({ slug: "", title: "", roles: ["owner", "manager", "cashier"], steps: [{ text: "", image_path: null, caption: "" }], keywords: [], synonyms: [], enabled: true, order: 999 });

export default function HelpAdmin({ open, onOpenChange }) {
  const [topics, setTopics] = useState([]);
  const [editing, setEditing] = useState(null);
  const [logs, setLogs] = useState(null);
  const [candidates, setCandidates] = useState([]);

  const load = () => api.get("/superadmin/help/topics").then((r) => setTopics(r.data)).catch(() => {});
  const loadCand = () => api.get("/superadmin/help/ai-candidates").then((r) => setCandidates(r.data)).catch(() => {});
  const loadLogs = () => api.get("/superadmin/help/logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { if (open) { load(); loadCand(); loadLogs(); } }, [open]);

  const promote = (c) => setEditing({
    ...blank(), title: (c.question || "").slice(0, 80),
    steps: [{ text: c.answer || "", image_path: null, caption: "" }],
    keywords: (c.question || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2),
    _promoteId: c.id, _fromQuestion: c.question,
  });
  const merge = async (c, topicId) => {
    try { await api.post(`/superadmin/help/ai-candidates/${c.id}/merge`, { topic_id: topicId }); toast.success("Merged into tutorial"); loadCand(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reject = async (c) => {
    if (!window.confirm("Reject this AI answer candidate?")) return;
    try { await api.delete(`/superadmin/help/ai-candidates/${c.id}`); loadCand(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startNew = () => setEditing(blank());
  const edit = (t) => setEditing({ ...t, keywords: t.keywords || [], synonyms: t.synonyms || [], steps: t.steps?.length ? t.steps : [{ text: "", image_path: null, caption: "" }] });

  const toggleEnabled = async (t) => {
    try { await api.put(`/superadmin/help/topics/${t.id}`, { enabled: !t.enabled }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete help topic "${t.title}"?`)) return;
    try { await api.delete(`/superadmin/help/topics/${t.id}`); toast.success("Topic deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    const body = { ...editing, order: parseInt(editing.order || 999), steps: (editing.steps || []).filter((s) => (s.text || "").trim() || s.image_path) };
    try {
      if (editing._promoteId) {
        await api.post(`/superadmin/help/ai-candidates/${editing._promoteId}/promote`, body);
        toast.success("Promoted to built-in tutorial"); setEditing(null); load(); loadCand();
      } else if (editing.id) {
        await api.put(`/superadmin/help/topics/${editing.id}`, body); toast.success("Topic saved"); setEditing(null); load();
      } else {
        await api.post("/superadmin/help/topics", body); toast.success("Topic saved"); setEditing(null); load();
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // step helpers
  const setStep = (i, k, v) => setEditing((e) => ({ ...e, steps: e.steps.map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const addStep = () => setEditing((e) => ({ ...e, steps: [...e.steps, { text: "", image_path: null, caption: "" }] }));
  const delStep = (i) => setEditing((e) => ({ ...e, steps: e.steps.filter((_, j) => j !== i) }));
  const moveStep = (i, d) => setEditing((e) => {
    const s = [...e.steps]; const j = i + d; if (j < 0 || j >= s.length) return e;
    [s[i], s[j]] = [s[j], s[i]]; return { ...e, steps: s };
  });
  const uploadStepImg = async (i, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/superadmin/help/image", fd); setStep(i, "image_path", data.image_path); toast.success("Screenshot added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="help-admin-modal">
        <DialogHeader><DialogTitle className="font-head">Help Content Management</DialogTitle></DialogHeader>

        {logs && (
          <div className="flex flex-wrap items-center gap-4 text-xs bg-secondary/40 border border-border rounded-lg px-3 py-2" data-testid="help-admin-stats">
            <BarChart3 className="w-4 h-4 text-emerald-500" />
            <span>Answers: <b className="text-emerald-600">Built-in: {logs.counts?.tutorial || 0}</b> · <b className="text-sky-600">AI: {logs.ai_total || 0}</b></span>
            <span>AI calls prevented by promoted guides: <b className="text-emerald-600">{logs.ai_prevented_by_promoted || 0}</b></span>
            {logs.top_topics?.[0] && <span>Most used: <b>{logs.top_topics[0].title}</b> ({logs.top_topics[0].hits})</span>}
          </div>
        )}

        {!editing && candidates.length > 0 && (
          <div className="space-y-2" data-testid="help-ai-queue">
            <p className="text-sm font-head font-bold flex items-center gap-1.5"><Bot className="w-4 h-4 text-sky-500" /> New AI Answers to Review ({candidates.length})</p>
            {candidates.map((c) => (
              <Card key={c.id} className="p-3 space-y-2 border-sky-500/30" data-testid={`help-ai-candidate-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm">{c.question}</p>
                  <span className="text-[10px] text-muted-foreground shrink-0">asked {c.count}× · {c.role} · {(c.last_at || "").slice(0, 10)}</span>
                </div>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap bg-secondary/50 rounded p-2">{c.answer}</p>
                <p className="text-[10px] text-muted-foreground">model: {c.model} · match score: {c.score} {c.similar_tutorials?.length > 0 && <>· similar: {c.similar_tutorials.map((s) => `${s.title} (${s.score})`).join(", ")}</>}</p>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => promote(c)} data-testid={`help-ai-promote-${c.id}`}>Approve &amp; Promote</Button>
                  {(c.similar_tutorials || []).slice(0, 2).map((s) => (
                    <Button key={s.id} size="sm" variant="outline" onClick={() => merge(c, s.id)} data-testid={`help-ai-merge-${c.id}-${s.id}`}>Merge → {s.title}</Button>
                  ))}
                  <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => reject(c)} data-testid={`help-ai-reject-${c.id}`}>Reject</Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {!editing ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">{topics.length} topics. Changes are live instantly. No deployment needed.</p>
              <Button size="sm" onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="help-admin-new"><Plus className="w-4 h-4 mr-1" />New Topic</Button>
            </div>
            {topics.map((t) => (
              <Card key={t.id} className="p-3 flex items-center justify-between gap-3" data-testid={`help-admin-topic-${t.slug}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{t.title} {!t.enabled && <span className="text-[10px] text-rose-500">(disabled)</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{(t.roles || []).join(", ")} · {(t.steps || []).length} steps · {(t.keywords || []).length} keywords</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => toggleEnabled(t)} data-testid={`help-admin-toggle-${t.slug}`}>{t.enabled ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => edit(t)} data-testid={`help-admin-edit-${t.slug}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => remove(t)} data-testid={`help-admin-delete-${t.slug}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="help-admin-editor">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="help-admin-title" /></div>
              <div><Label className="text-xs">Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
            </div>
            <div>
              <Label className="text-xs">Who can use this feature (role visibility)</Label>
              <div className="flex gap-3 mt-1">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-1.5 text-sm capitalize">
                    <input type="checkbox" checked={editing.roles.includes(r)} onChange={(e) => setEditing((ed) => ({ ...ed, roles: e.target.checked ? [...ed.roles, r] : ed.roles.filter((x) => x !== r) }))} data-testid={`help-admin-role-${r}`} />{r}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Keywords (comma separated)</Label><Input value={(editing.keywords || []).join(", ")} onChange={(e) => setEditing({ ...editing, keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="help-admin-keywords" /></div>
              <div><Label className="text-xs">Synonyms / misspellings (comma separated)</Label><Input value={(editing.synonyms || []).join(", ")} onChange={(e) => setEditing({ ...editing, synonyms: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="help-admin-synonyms" /></div>
            </div>
            <div className="w-28"><Label className="text-xs">Order</Label><Input type="number" integer value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>

            <div>
              <div className="flex justify-between items-center mb-1"><Label className="text-xs">Steps &amp; screenshots</Label><Button size="sm" variant="outline" onClick={addStep} data-testid="help-admin-add-step"><Plus className="w-3.5 h-3.5 mr-1" />Add step</Button></div>
              <div className="space-y-2">
                {editing.steps.map((s, i) => (
                  <Card key={i} className="p-3 space-y-2" data-testid={`help-admin-step-${i}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold mt-2 w-5">{i + 1}.</span>
                      <Textarea rows={2} value={s.text} onChange={(e) => setStep(i, "text", e.target.value)} placeholder="Step instruction" className="flex-1" />
                      <div className="flex flex-col gap-1">
                        <Button size="icon" variant="ghost" onClick={() => moveStep(i, -1)} data-testid={`help-admin-step-up-${i}`}><ChevronUp className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => moveStep(i, 1)} data-testid={`help-admin-step-down-${i}`}><ChevronDown className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => delStep(i)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pl-7">
                      {s.image_path ? (
                        <div className="flex items-center gap-2">
                          <img src={IMG(s.image_path)} alt="step" className="h-14 rounded border border-border" />
                          <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => setStep(i, "image_path", null)}><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground rounded-lg border border-dashed border-border px-2.5 py-1.5 hover:bg-secondary">
                          <ImageIcon className="w-4 h-4" /> Add screenshot
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadStepImg(i, e.target.files?.[0])} data-testid={`help-admin-step-img-${i}`} />
                        </label>
                      )}
                      <Input value={s.caption || ""} onChange={(e) => setStep(i, "caption", e.target.value)} placeholder="Caption / where to click (optional)" className="flex-1 text-xs" />
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditing(null)} data-testid="help-admin-cancel">Cancel</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="help-admin-save">Save Topic</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
